import React, { useEffect, useRef, useState } from "react";
import { Tv, User, Settings as SettingsIcon, ChevronLeft, ChevronRight, Users, BarChart2, PieChart, Film, Calendar, Camera, Sun, Moon, LogOut, Contact, Sparkles, X, Shield, BookOpen } from "lucide-react";
import { cn } from "../utils";
import { motion, AnimatePresence, useAnimation } from "motion/react";
import { ACCESS_ROLE_OPTIONS, getRoleLabel, hasPermission as profileHasPermission } from "../lib/accessControl";
import { UserRole } from "../types";

const SIDEBAR_EXPANDED_STORAGE_KEY = "project-tools:sidebar-expanded";

interface SidebarProps {
  currentView: "welcome" | "dashboard" | "project" | "settings" | "crm" | "finance" | "gantt" | "people" | "calendar" | "onset" | "wiki";
  onNavigate: (view: "welcome" | "dashboard" | "project" | "settings" | "crm" | "finance" | "gantt" | "people" | "calendar" | "onset" | "wiki") => void;
  user: any;
  profile?: {
    role?: string;
    status?: string;
    customPermissions?: string[];
  } | null;
  darkMode: boolean;
  toggleDarkMode: () => void;
  isFirebaseConnected: boolean | null;
  onLogout: () => Promise<void>;
  isPreviewing?: boolean;
  previewRole?: UserRole | null;
  onPreviewRoleChange?: (role: UserRole | null) => void;
  onClearPreview?: () => void;
}

const NavTab = ({ tab, isActive, currentView, onNavigate, isExpanded }: { tab: any, isActive: boolean, currentView: string, onNavigate: any, isExpanded: boolean }) => {
  const controls = useAnimation();
  const Icon = tab.icon;

  const handleMouseEnter = () => {
    controls.start({
      rotate: [0, -15, 15, -15, 0],
      scale: 1.1,
      transition: { duration: 0.5, ease: "easeInOut" }
    });
  };

  const handleMouseLeave = () => {
    controls.start({
      rotate: 0,
      scale: 1,
      transition: { duration: 0.3, ease: "easeInOut" }
    });
  };

  return (
    <motion.button
      key={tab.id}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      whileTap={{ scale: 0.98 }}
      onClick={() => tab.targetView && onNavigate(tab.targetView)}
      className={cn(
        "group relative flex items-center h-12 rounded-md transition-all duration-200",
        isActive 
          ? "bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-slate-100" 
          : "text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-slate-700 dark:hover:text-slate-200"
      )}
    >
      <div className="w-12 h-12 flex items-center justify-center shrink-0">
        <div 
          className={cn(
            "w-[25px] h-[25px] rounded flex items-center justify-center transition-all duration-300 shadow-sm",
            isActive ? "ring-2 ring-offset-2 ring-slate-200 dark:ring-slate-700 dark:ring-offset-slate-900" : ""
          )}
          style={{ backgroundColor: tab.color }}
        >
          <motion.div
            animate={controls}
            initial={{ scale: 1, rotate: 0 }}
          >
            <Icon className="w-[16px] h-[16px] text-white" />
          </motion.div>
        </div>
      </div>

      <AnimatePresence mode="wait">
        {isExpanded && (
          <motion.span
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -10 }}
            className="ml-[3px] font-brandon font-normal text-base whitespace-nowrap overflow-hidden"
          >
            {tab.label}
          </motion.span>
        )}
      </AnimatePresence>
    </motion.button>
  );
};

export function Sidebar({
  currentView,
  onNavigate,
  user,
  profile,
  darkMode,
  toggleDarkMode,
  isFirebaseConnected,
  onLogout,
  isPreviewing = false,
  previewRole = null,
  onPreviewRoleChange,
  onClearPreview,
}: SidebarProps) {
  const [isExpanded, setIsExpanded] = useState(() => {
    if (typeof window === "undefined") return true;
    const stored = window.localStorage.getItem(SIDEBAR_EXPANDED_STORAGE_KEY);
    if (stored === null) return true;
    return stored === "true";
  });
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isAccountSettingsOpen, setIsAccountSettingsOpen] = useState(false);
  const logoControls = useAnimation();
  const settingsControls = useAnimation();
  const themeControls = useAnimation();
  const collapseControls = useAnimation();
  const profileMenuRef = useRef<HTMLDivElement | null>(null);

  const tabs = [
    {
      id: "finance",
      label: "Finance",
      icon: PieChart,
      views: ["finance"],
      targetView: "finance" as const,
      color: "#d4a106",
    },
    {
      id: "analytics",
      label: "Analytics",
      icon: BarChart2,
      views: ["gantt"],
      targetView: "gantt" as const,
      color: "#ff8600",
    },
    {
      id: "projects",
      label: "Projects",
      icon: Tv,
      views: ["dashboard", "project"],
      targetView: "dashboard" as const,
      color: "#e4a4a5",
    },
    {
      id: "crm",
      label: "CRM",
      icon: Contact,
      views: ["crm"],
      targetView: "crm" as const,
      color: "#234d6f",
    },
    {
      id: "people",
      label: "People",
      icon: Users,
      views: ["people"],
      targetView: "people" as const,
      color: "#597058",
    },
    {
      id: "wiki",
      label: "Wiki",
      icon: BookOpen,
      views: ["wiki"],
      targetView: "wiki" as const,
      color: "#7a6ff0",
    },
    {
      id: "calendar",
      label: "Calendar",
      icon: Calendar,
      views: ["calendar"],
      targetView: "calendar" as const,
      color: "#c03831",
    },
    {
      id: "onset",
      label: "Onset",
      icon: Camera,
      views: ["onset"],
      targetView: "onset" as const,
      color: "#3397da",
    },
  ];

  const appKeyColor = "#ffcc01";
  const accessLabel = profile?.role ? getRoleLabel(profile.role as UserRole) : "Team Member";
  const visibleTabs = tabs.filter((tab) => {
    if (tab.id === "finance") return profile?.role === "admin" || profile?.role === "super_admin";
    if (tab.id === "crm") return profileHasPermission(profile as any, "crm.view");
    if (tab.id === "people") return profile?.role === "operations" || profile?.role === "super_admin";
    if (tab.id === "wiki") return profileHasPermission(profile as any, "wiki.view");
    if (tab.id === "calendar") return profileHasPermission(profile as any, "calendar.view");
    return true;
  });
  const canViewSettings =
    profileHasPermission(profile as any, "settings.manage") ||
    profileHasPermission(profile as any, "integrations.manage") ||
    profileHasPermission(profile as any, "drive.manage") ||
    profileHasPermission(profile as any, "users.manage") ||
    profileHasPermission(profile as any, "trash.manage");
  const canPreviewRoles = user && !isPreviewing && (profile?.role === "admin" || profile?.role === "super_admin");

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      if (!profileMenuRef.current?.contains(event.target as Node)) {
        setIsProfileMenuOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsProfileMenuOpen(false);
        setIsAccountSettingsOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(SIDEBAR_EXPANDED_STORAGE_KEY, String(isExpanded));
  }, [isExpanded]);

  return (
    <motion.div 
      initial={false}
      animate={{ width: isExpanded ? 240 : 60 }}
      className="z-40 h-screen bg-white dark:bg-slate-900 flex flex-col border-r border-slate-200 dark:border-slate-800 shadow-sm shrink-0 sticky top-0 transition-colors duration-300"
    >
      {/* Logo Area */}
      <div className="h-16 flex items-center px-1.5 shrink-0">
        <div 
          onClick={() => onNavigate("welcome")}
          onMouseEnter={() => logoControls.start({ rotate: [0, -15, 15, -15, 0], scale: 1.1, transition: { duration: 0.5, ease: "easeInOut" } })}
          onMouseLeave={() => logoControls.start({ rotate: 0, scale: 1, transition: { duration: 0.3, ease: "easeInOut" } })}
          className="w-12 h-12 flex items-center justify-center shrink-0 cursor-pointer"
        >
          <div 
            className="w-[25px] h-[25px] rounded flex items-center justify-center shrink-0 shadow-sm mt-[10px]"
            style={{ backgroundColor: appKeyColor }}
          >
            <motion.div
              animate={logoControls}
              initial={{ scale: 1, rotate: 0 }}
            >
              <Film className="w-[16px] h-[16px] text-white" />
            </motion.div>
          </div>
        </div>
        <AnimatePresence mode="wait">
          {isExpanded && (
            <motion.span
              onClick={() => onNavigate("welcome")}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -10 }}
              className="ml-[3px] cursor-pointer font-brandon font-medium text-[#3397da] text-[24px] whitespace-nowrap overflow-hidden mt-[10px]"
            >
              Project Tools
            </motion.span>
          )}
        </AnimatePresence>
      </div>

      {/* Navigation Items */}
      <div className="flex-1 pt-6 pb-6 flex flex-col gap-[3px] px-1.5 overflow-y-auto overflow-x-hidden">
        {visibleTabs.map((tab) => (
          <NavTab 
            key={tab.id} 
            tab={tab} 
            isActive={tab.views.includes(currentView)} 
            currentView={currentView} 
            onNavigate={onNavigate} 
            isExpanded={isExpanded} 
          />
        ))}
      </div>

      {/* Bottom Actions */}
      <div className="p-1.5 flex flex-col gap-2">
        <motion.button
          onMouseEnter={() => collapseControls.start({ x: isExpanded ? -4 : 4, transition: { duration: 0.2 } })}
          onMouseLeave={() => collapseControls.start({ x: 0, transition: { duration: 0.2 } })}
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center h-12 rounded-md text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-slate-700 dark:hover:text-slate-200 transition-all duration-200"
        >
          <div className="w-12 h-12 flex items-center justify-center shrink-0">
            <motion.div 
              animate={collapseControls}
              initial={{ x: 0 }}
            >
              {isExpanded ? <ChevronLeft className="w-[18px] h-[18px]" /> : <ChevronRight className="w-[18px] h-[18px]" />}
            </motion.div>
          </div>
          <AnimatePresence mode="wait">
            {isExpanded && (
              <motion.span
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                className="ml-[3px] font-brandon font-normal text-base whitespace-nowrap overflow-hidden"
              >
                Collapse
              </motion.span>
            )}
          </AnimatePresence>
        </motion.button>

        <motion.button
          onMouseEnter={() => themeControls.start({ rotate: 45, scale: 1.1, transition: { duration: 0.4, ease: "easeInOut" } })}
          onMouseLeave={() => themeControls.start({ rotate: 0, scale: 1, transition: { duration: 0.3, ease: "easeInOut" } })}
          whileTap={{ scale: 0.95 }}
          onClick={() => {
            console.log("Sidebar toggle clicked");
            toggleDarkMode();
          }}
          className="group relative flex items-center h-12 rounded-md text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-slate-700 dark:hover:text-slate-300 transition-all duration-200"
        >
          <div className="w-12 h-12 flex items-center justify-center shrink-0">
            <div 
              className="w-7 h-7 rounded flex items-center justify-center transition-all duration-300 shadow-sm bg-slate-100 dark:bg-slate-800"
            >
              <motion.div
                animate={themeControls}
                initial={{ scale: 1, rotate: 0 }}
              >
                {darkMode ? <Sun className="w-[18px] h-[18px] text-yellow-400" /> : <Moon className="w-[18px] h-[18px] text-slate-600" />}
              </motion.div>
            </div>
          </div>
          <AnimatePresence mode="wait">
            {isExpanded && (
              <motion.span
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                className="ml-[3px] font-brandon font-normal text-base whitespace-nowrap overflow-hidden"
              >
                {darkMode ? "Light Mode" : "Dark Mode"}
              </motion.span>
            )}
          </AnimatePresence>
        </motion.button>

        {canViewSettings && (
          <motion.button
            onMouseEnter={() => settingsControls.start({ rotate: 90, scale: 1.1, transition: { duration: 0.4, ease: "easeInOut" } })}
            onMouseLeave={() => settingsControls.start({ rotate: 0, scale: 1, transition: { duration: 0.3, ease: "easeInOut" } })}
            whileTap={{ scale: 0.95 }}
            onClick={() => onNavigate("settings")}
            className={cn(
              "group relative flex items-center h-12 rounded-md transition-all duration-200",
              currentView === "settings"
                ? "bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                : "text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-slate-700 dark:hover:text-slate-200"
            )}
          >
            <div className="w-12 h-12 flex items-center justify-center shrink-0">
              <div 
                className={cn(
                  "w-7 h-7 rounded flex items-center justify-center transition-all duration-300 shadow-sm",
                  currentView === "settings" ? "ring-2 ring-offset-2 ring-slate-200 dark:ring-slate-700 dark:ring-offset-slate-900" : ""
                )}
                style={{ backgroundColor: "#c4beb5" }}
              >
                <motion.div
                  animate={settingsControls}
                  initial={{ scale: 1, rotate: 0 }}
                >
                  <SettingsIcon className="w-[18px] h-[18px] text-white" />
                </motion.div>
              </div>
            </div>
            <AnimatePresence mode="wait">
              {isExpanded && (
                <motion.span
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  className="ml-[3px] font-brandon font-normal text-base whitespace-nowrap overflow-hidden"
                >
                  Settings
                </motion.span>
              )}
            </AnimatePresence>
          </motion.button>
        )}

        {/* User Profile */}
        {user && (
          <div className="mt-2 pt-2 border-t border-slate-200 dark:border-slate-800">
            {/* Firebase Status */}
            <div className={cn(
              "flex items-center h-10 mx-1.5 rounded-md transition-all duration-200 mb-2",
              isExpanded ? "px-1.5" : "justify-center",
              isFirebaseConnected === true ? "bg-emerald-50/50 dark:bg-emerald-900/10" : "bg-rose-50/50 dark:bg-rose-900/10"
            )}>
              <div className="flex h-10 w-10 shrink-0 items-center justify-center">
                <div className={cn(
                  "w-2 h-2 rounded-full",
                  isFirebaseConnected === true ? "bg-emerald-500" : "bg-rose-500 animate-pulse"
                )} />
              </div>
              <AnimatePresence mode="wait">
                {isExpanded && (
                  <motion.div
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -10 }}
                    className="ml-2 flex flex-col min-w-0"
                  >
                    <span className={cn(
                      "text-[10px] font-bold uppercase tracking-wider whitespace-nowrap",
                      isFirebaseConnected === true ? "text-emerald-700 dark:text-emerald-400" : "text-rose-700 dark:text-rose-400"
                    )}>
                      {isFirebaseConnected === true ? "Firebase Connected" : "Firebase Error"}
                    </span>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <div ref={profileMenuRef} className="relative">
              <button
                type="button"
                onClick={() => setIsProfileMenuOpen(prev => !prev)}
                className={cn(
                  "w-full flex items-center h-12 px-1 rounded-xl transition-all duration-200",
                  isProfileMenuOpen
                    ? "bg-slate-100 dark:bg-slate-800/80"
                    : "hover:bg-slate-50 dark:hover:bg-slate-800/60"
                )}
                aria-haspopup="menu"
                aria-expanded={isProfileMenuOpen}
                title={isExpanded ? "Open account menu" : user.displayName || user.email || "Open account menu"}
              >
                <div className="w-10 h-10 rounded-full overflow-hidden shrink-0 border border-slate-200 dark:border-slate-700 shadow-sm">
                  <img 
                    src={user.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.displayName || user.email || "User")}&background=ffcc01&color=fff`} 
                    alt="Profile" 
                    className="w-full h-full object-cover"
                    referrerPolicy="no-referrer"
                  />
                </div>
                <AnimatePresence mode="wait">
                  {isExpanded && (
                    <motion.div
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -10 }}
                      className="ml-[3px] flex items-center justify-between flex-1 min-w-0 overflow-hidden"
                    >
                      <div className="flex flex-col min-w-0 overflow-hidden text-left">
                        <span className="text-xs font-bold text-slate-900 dark:text-slate-100 truncate">
                          {user.displayName || "User"}
                        </span>
                        <span className="text-[10px] text-slate-500 dark:text-slate-400 truncate">
                          {user.email}
                        </span>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </button>

              <AnimatePresence>
                {isProfileMenuOpen && (
                  <motion.div
                    initial={{ opacity: 0, x: -8, scale: 0.98 }}
                    animate={{ opacity: 1, x: 0, scale: 1 }}
                    exit={{ opacity: 0, x: -8, scale: 0.98 }}
                    transition={{ duration: 0.16, ease: "easeOut" }}
                    className="absolute bottom-0 left-full z-[120] ml-3 w-64 rounded-2xl border border-slate-200 bg-white/95 shadow-2xl backdrop-blur-md dark:border-slate-700 dark:bg-slate-900/95"
                    role="menu"
                  >
                    <div className="p-3">
                      <div className="flex items-center gap-3 rounded-xl bg-slate-50 px-3 py-3 dark:bg-slate-800/70">
                        <div className="h-11 w-11 rounded-full overflow-hidden shrink-0 border border-slate-200 shadow-sm dark:border-slate-700">
                          <img
                            src={user.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.displayName || user.email || "User")}&background=ffcc01&color=fff`}
                            alt="Profile"
                            className="h-full w-full object-cover"
                            referrerPolicy="no-referrer"
                          />
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs font-semibold text-slate-900 dark:text-slate-100 truncate">
                            {user.displayName || "User"}
                          </p>
                          <p className="text-[11px] text-slate-500 dark:text-slate-400 truncate">
                            {user.email}
                          </p>
                          <p className="mt-1 text-[11px] font-semibold text-slate-500 dark:text-slate-400">
                            {accessLabel}
                          </p>
                        </div>
                      </div>

                      <div className="mt-3">
                        <button
                          type="button"
                          onClick={() => {
                            setIsProfileMenuOpen(false);
                            setIsAccountSettingsOpen(true);
                          }}
                          className="w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-100 transition-colors dark:text-slate-200 dark:hover:bg-slate-800"
                          role="menuitem"
                        >
                          <User className="w-4 h-4" />
                          Account Settings
                        </button>

                        <button
                          type="button"
                          onClick={() => {
                            setIsProfileMenuOpen(false);
                            onNavigate("settings");
                          }}
                          className="w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-100 transition-colors dark:text-slate-200 dark:hover:bg-slate-800"
                          role="menuitem"
                        >
                          <SettingsIcon className="w-4 h-4" />
                          Open App Settings
                        </button>

                        <button
                          type="button"
                          onClick={async () => {
                            setIsProfileMenuOpen(false);
                            await onLogout();
                          }}
                          className="mt-1 w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-100 transition-colors dark:text-slate-300 dark:hover:bg-slate-800"
                          role="menuitem"
                        >
                          <LogOut className="w-4 h-4" />
                          Sign Out
                        </button>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        )}
      </div>

      <AnimatePresence>
        {isAccountSettingsOpen && user && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[140] flex items-center justify-center bg-slate-900/55 p-4 backdrop-blur-sm"
            onClick={() => setIsAccountSettingsOpen(false)}
          >
            <motion.div
              initial={{ opacity: 0, y: 12, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 12, scale: 0.98 }}
              transition={{ duration: 0.18, ease: "easeOut" }}
              className="w-full max-w-3xl overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="flex items-start justify-between border-b border-slate-200 px-6 py-5 dark:border-slate-800">
                <div>
                  <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Account Settings</h2>
                  <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                    Your account tools, preview controls, and personal shortcuts live here.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setIsAccountSettingsOpen(false)}
                  className="rounded-xl p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200"
                  aria-label="Close account settings"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="grid gap-6 p-6 lg:grid-cols-[1.1fr_0.9fr]">
                <div className="space-y-6">
                  <div className="rounded-2xl bg-slate-50 p-5 dark:bg-slate-800/70">
                    <div className="flex items-center gap-4">
                      <div className="h-16 w-16 overflow-hidden rounded-2xl border border-slate-200 shadow-sm dark:border-slate-700">
                        <img
                          src={user.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.displayName || user.email || "User")}&background=ffcc01&color=fff`}
                          alt="Profile"
                          className="h-full w-full object-cover"
                          referrerPolicy="no-referrer"
                        />
                      </div>
                      <div className="min-w-0">
                        <p className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                          {user.displayName || "User"}
                        </p>
                        <p className="truncate text-sm text-slate-500 dark:text-slate-400">{user.email}</p>
                        <div className="mt-3 flex flex-wrap gap-2">
                          <span className="rounded-full bg-slate-200 px-3 py-1 text-[11px] font-semibold text-slate-700 dark:bg-slate-700 dark:text-slate-200">
                            {accessLabel}
                          </span>
                          {isPreviewing && previewRole && (
                            <span className="rounded-full bg-amber-100 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-amber-800">
                              Previewing {previewRole}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {(canPreviewRoles || isPreviewing) && (
                    <div className="rounded-2xl border border-slate-200 p-5 dark:border-slate-700">
                      <div className="mb-4 flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3">
                          <div className="rounded-2xl bg-amber-100 p-2 text-amber-700">
                            <Sparkles className="h-5 w-5" />
                          </div>
                          <div>
                            <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">UI Preview Mode</h3>
                            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                              Switch the interface to another role so you can see what teammates can access while we build.
                            </p>
                          </div>
                        </div>
                        {isPreviewing && onClearPreview && (
                          <button
                            type="button"
                            onClick={onClearPreview}
                            className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800 transition-colors hover:bg-amber-100"
                          >
                            Reset Preview
                          </button>
                        )}
                      </div>

                      <div className="grid gap-3 md:grid-cols-2">
                        {ACCESS_ROLE_OPTIONS.map((option) => {
                          const isSelected = previewRole === option.value;
                          return (
                            <button
                              key={option.value}
                              type="button"
                              onClick={() => onPreviewRoleChange?.(option.value)}
                              className={cn(
                                "rounded-2xl border px-4 py-3 text-left transition-colors",
                                isSelected
                                  ? "border-amber-300 bg-amber-50"
                                  : "border-slate-200 hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800"
                              )}
                            >
                              <div className="flex items-center justify-between gap-3">
                                <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{option.label}</p>
                                {isSelected && (
                                  <span className="rounded-full bg-amber-200 px-2 py-1 text-[10px] font-bold uppercase tracking-[0.16em] text-amber-900">
                                    Active
                                  </span>
                                )}
                              </div>
                              <p className="mt-2 text-xs leading-5 text-slate-500 dark:text-slate-400">
                                {option.description}
                              </p>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                <div className="space-y-4">
                  <div className="rounded-2xl border border-slate-200 p-5 dark:border-slate-700">
                    <div className="flex items-start gap-3">
                      <div className="rounded-2xl bg-slate-100 p-2 text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                        <Shield className="h-5 w-5" />
                      </div>
                      <div>
                        <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">Workspace Controls</h3>
                        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                          Quick links for the things you’re most likely to adjust while administering the app.
                        </p>
                      </div>
                    </div>

                    <div className="mt-4 space-y-2">
                      <button
                        type="button"
                        onClick={() => {
                          setIsAccountSettingsOpen(false);
                          onNavigate("settings");
                        }}
                        className="w-full rounded-xl border border-slate-200 px-4 py-3 text-left transition-colors hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800"
                      >
                        <p className="text-sm font-medium text-slate-900 dark:text-slate-100">Open App Settings</p>
                        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Manage global rates, integrations, drive folders, domain users, and deleted projects.</p>
                      </button>

                      <button
                        type="button"
                        onClick={toggleDarkMode}
                        className="w-full rounded-xl border border-slate-200 px-4 py-3 text-left transition-colors hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800"
                      >
                        <p className="text-sm font-medium text-slate-900 dark:text-slate-100">{darkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}</p>
                        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">This only changes your current local view of the app.</p>
                      </button>

                      <button
                        type="button"
                        onClick={async () => {
                          setIsAccountSettingsOpen(false);
                          await onLogout();
                        }}
                        className="w-full rounded-xl border border-slate-200 px-4 py-3 text-left transition-colors hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800"
                      >
                        <p className="text-sm font-medium text-slate-900 dark:text-slate-100">Sign Out</p>
                        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">End this session and return to the login screen.</p>
                      </button>
                    </div>
                  </div>

                  <div className={cn(
                    "rounded-2xl border p-5",
                    isFirebaseConnected === true
                      ? "border-emerald-200 bg-emerald-50/80"
                      : "border-rose-200 bg-rose-50/80"
                  )}>
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                      Environment
                    </p>
                    <p className="mt-2 text-sm font-semibold text-slate-900">
                      {isFirebaseConnected === true ? "Firebase Connected" : "Firebase Needs Attention"}
                    </p>
                    <p className="mt-1 text-xs leading-5 text-slate-600">
                      {isFirebaseConnected === true
                        ? "Your local app is currently talking to Firebase successfully."
                        : "The app can’t currently confirm Firebase connectivity, so some data may not load correctly."}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
